import React, { useState, useEffect, useRef, useCallback } from 'react';
import type * as atlas from 'azure-maps-control';
import { FindhelpProgram } from '@src/api.types';
import { UniteUsProgram } from '@repo/ui';

// Constants
const MAP_CONTAINER_ID = 'advocate-cbo-map';
const MAP_ZOOM_LEVEL = 10;
const MAP_STYLE = 'road';
const POPUP_PIXEL_OFFSET: [number, number] = [0, -30];
const MAP_PADDING = 50;
const CAMERA_ANIMATION_DURATION = 1000;
const MARKER_DEFAULT_COLOR = '#196ECF';
const MARKER_SELECTED_COLOR = '#B20000';
const MARKER_SIZE = 32;
const MARKER_SELECTED_SIZE = 48;

export type MarkerTarget = {
  id: string;
  name: string;
  distance: number | null;
  position: {
    lat: number;
    lng: number;
  };
};

export function uniteusIntoMarkerTarget(
  pgm: UniteUsProgram
): MarkerTarget | undefined {
  const location = pgm.document.locations?.[0];
  if (!location) return undefined;

  return {
    id: pgm.id,
    name: pgm.document.name,
    distance: null,
    position: {
      lat: location.latitude,
      lng: location.longitude,
    },
  };
}

export function intoMarkerTarget(
  pgm: FindhelpProgram
): MarkerTarget | undefined {
  const office = pgm.offices?.[0];
  if (!office?.location) return undefined;

  return {
    id: pgm.id,
    name: pgm.name,
    distance: office.distance,
    position: {
      lat: office.location.latitude,
      lng: office.location.longitude,
    },
  };
}

export type CboMapAzureProps = {
  subscriptionKey: string;
  programSearchResults: MarkerTarget[];
  onClickViewProgramLink: (clickedProgramId: string) => void;
};

function createMarkerHtml(color: string): string {
  return `<span style="display: inline-block; width: ${MARKER_SIZE}px; height: ${MARKER_SIZE}px; cursor: pointer; transform-origin: bottom center;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${MARKER_SIZE}" height="${MARKER_SIZE}" aria-hidden="true"><path fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#fff"/></svg></span>`;
}

function createSelectedMarkerHtml(color: string): string {
  return `<span style="display: inline-block; width: ${MARKER_SELECTED_SIZE}px; height: ${MARKER_SELECTED_SIZE}px; cursor: pointer; transform-origin: bottom center; transition: width 0.15s ease, height 0.15s ease;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${MARKER_SELECTED_SIZE}" height="${MARKER_SELECTED_SIZE}" aria-hidden="true"><path fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#fff"/></svg></span>`;
}

// Helper function to create popup HTML content
function createPopupContent(markerData: MarkerTarget): string {
  const distanceText = markerData.distance !== null
    ? `${markerData.distance} miles away`
    : 'Distance unavailable';

  return `
    <div style="padding: 10px; min-width: 200px; max-width: 280px;">
      <h3 style="margin: 0 0 10px 0; font-size: 16px; font-weight: 600; line-height: 1.25; white-space: normal; overflow-wrap: anywhere; word-break: break-word; max-height: 2.5em; overflow: hidden;">${markerData.name}</h3>
      <div style="display: flex; align-items: center; margin-bottom: 10px;">
        <span style="margin-right: 5px;">📍</span>
        <span>${distanceText}</span>
      </div>
      <a
        href="#"
        data-cy="map-view-program-link-${markerData.id}"
        data-program-id="${markerData.id}"
        style="color: #1a73e8; text-decoration: underline; cursor: pointer;"
      >
        View Details
      </a>
    </div>
  `;
}

export function CboMapAzure({
  subscriptionKey,
  programSearchResults,
  onClickViewProgramLink,
}: CboMapAzureProps): React.JSX.Element {
  const [isMounted, setIsMounted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [atlasModule, setAtlasModule] = useState<typeof atlas | null>(null);

  const mapRef = useRef<atlas.Map | null>(null);
  const markersRef = useRef<atlas.HtmlMarker[]>([]);
  const popupRef = useRef<atlas.Popup | null>(null);
  const selectedMarkerRef = useRef<atlas.HtmlMarker | null>(null);

  // Set mounted flag on client side only
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Initialize Azure Maps (client-side only to avoid SSR issues)
  useEffect(() => {
    if (!isMounted || !subscriptionKey || mapRef.current) return;

    let map: atlas.Map | null = null;

    import('azure-maps-control')
      .then((atlasImport) => {
        setAtlasModule(atlasImport);

        map = new atlasImport.Map(MAP_CONTAINER_ID, {
          authOptions: {
            authType: atlasImport.AuthenticationType.subscriptionKey,
            subscriptionKey: subscriptionKey,
          },
          center: [0, 0],
          zoom: MAP_ZOOM_LEVEL,
          style: MAP_STYLE,
          language: 'en-US',
        });

        map.events.add('ready', () => {
          if (!map) return;

          setIsLoaded(true);
          mapRef.current = map;

          const popup = new atlasImport.Popup({
            pixelOffset: POPUP_PIXEL_OFFSET,
            closeButton: true,
          });
          popupRef.current = popup;
        });
      })
      .catch((error) => {
        console.error('Failed to load Azure Maps:', error);
      });

    return () => {
      if (map) {
        map.dispose();
      }
    };
  }, [isMounted, subscriptionKey]);

  // Handle popup link clicks using event delegation
  const handlePopupClick = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' && target.dataset.programId) {
      e.preventDefault();
      onClickViewProgramLink(target.dataset.programId);
    }
  }, [onClickViewProgramLink]);

  // Setup event delegation for popup clicks
  useEffect(() => {
    document.addEventListener('click', handlePopupClick);
    return () => {
      document.removeEventListener('click', handlePopupClick);
    };
  }, [handlePopupClick]);

  // Match member behavior: selecting a pin should also open/focus the
  // corresponding program card, not only show the popup.
  const handleMarkerClick = useCallback((markerData: MarkerTarget, position: atlas.data.Position, marker: atlas.HtmlMarker) => {
    if (!popupRef.current || !mapRef.current) return;

    if (selectedMarkerRef.current && selectedMarkerRef.current !== marker) {
      selectedMarkerRef.current.setOptions({
        htmlContent: createMarkerHtml(MARKER_DEFAULT_COLOR),
      });
    }

    marker.setOptions({
      htmlContent: createSelectedMarkerHtml(MARKER_SELECTED_COLOR),
    });
    selectedMarkerRef.current = marker;

    // Pan to the selected marker
    mapRef.current.setCamera({
      center: position,
      type: 'ease',
      duration: CAMERA_ANIMATION_DURATION,
    });

    const popupContent = createPopupContent(markerData);
    popupRef.current.setOptions({
      content: popupContent,
      position: position,
    });
    popupRef.current.open(mapRef.current);
    onClickViewProgramLink(markerData.id);
  }, [onClickViewProgramLink]);

  // Create a single marker
  const createMarker = useCallback((markerData: MarkerTarget): atlas.HtmlMarker | null => {
    if (!atlasModule || !mapRef.current) return null;

    // Convert {lat, lng} to [lng, lat] (GeoJSON format)
    const position: atlas.data.Position = [
      markerData.position.lng,
      markerData.position.lat
    ];

    const marker = new atlasModule.HtmlMarker({
      position: position,
      htmlContent: createMarkerHtml(MARKER_DEFAULT_COLOR),
    });

    mapRef.current.events.add('click', marker, () => handleMarkerClick(markerData, position, marker));
    mapRef.current.markers.add(marker);

    return marker;
  }, [atlasModule, handleMarkerClick]);

  // Update markers when search results change
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !atlasModule || !programSearchResults) return;

    const map = mapRef.current;

    // Clear existing markers
    markersRef.current.forEach((marker) => map.markers.remove(marker));
    markersRef.current = [];
    selectedMarkerRef.current = null;
    popupRef.current?.close();

    if (programSearchResults.length === 0) return;

    // Convert positions to GeoJSON format
    const positions: atlas.data.Position[] = programSearchResults.map((result) => [
      result.position.lng,
      result.position.lat,
    ]);

    // Calculate bounding box for all markers
    const bounds = atlasModule.data.BoundingBox.fromPositions(positions);

    // Create markers for each program
    const newMarkers = programSearchResults
      .map(createMarker)
      .filter((marker): marker is atlas.HtmlMarker => marker !== null);

    markersRef.current = newMarkers;

    // Auto-fit map to show all markers
    map.setCamera({
      bounds: bounds,
      padding: MAP_PADDING,
      type: 'ease',
      duration: CAMERA_ANIMATION_DURATION,
      maxZoom: 14,
    });
  }, [isLoaded, programSearchResults, atlasModule, createMarker]);

  return (
    <div>
      {!isLoaded && <h1>Loading...</h1>}
      <div
        id={MAP_CONTAINER_ID}
        style={{
          width: '100%',
          height: '50vh',
          display: isLoaded ? 'block' : 'none',
        }}
      />
    </div>
  );
}